import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, IconButton, InputBase, Button, Typography } from '@mui/material';
import { SearchOutlinedIcon, ReplyOutlinedIcon } from 'icons/materialIcons';
import CreateCardIndividualOrTeamMemberModal from 'components/editCard/editCardTopNavbar/CreateCardIndividualOrTeamMemberModal';
import UpgradeSnackBar from 'components/common/notification/UpgradeSnackBar';
import ShareYourCardModal from 'components/common/ShareYourCardModal';
import { v4 as uuidv4 } from 'uuid';
import NewCardModal from 'components/editCard/editCardTopNavbar/NewCardModal';

const MyCardsNavbar = ({ onSearch }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const plusButtonRef = useRef(null);
  const navigate = useNavigate();
  const tempCardId = uuidv4();
  const profileUrl = "";

  const [isNewCardModalOpen, setIsNewCardModalOpen] = useState(false);

  const handlePlusClick = () => {
    setIsNewCardModalOpen(true);
  };

  const handleCreateNewCard = () => {
    navigate(`/dashboard/mycards/add-card/${tempCardId}?action=create`);
    setIsNewCardModalOpen(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleNewCardModalClose = () => {
    setIsNewCardModalOpen(false);
  };

  const handleShareModalOpen = () => {
    setIsShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
  };

  const handleCardTypeSelect = (type) => {
    if (type === 'team-member') {
      setSnackbarOpen(true);
    } else if (type === 'myself') {
      navigate(`/dashboard/mycards/add-card/${tempCardId}?action=create`);
    }
    setIsModalOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSubscribe = () => {
    setSnackbarOpen(false);
    navigate('/dashboard/subscribe');
  };

  const handleSearchChange = (event) => {
    onSearch(event.target.value);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm="auto">
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                My Cards
              </Typography>
            </Grid>
            <Grid item>
              <IconButton
                ref={plusButtonRef}
                onClick={handlePlusClick}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#E0E0E0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: '#d0d0d0',
                  },
                }}
              >
                <Typography variant="h6" sx={{ color: '#333' }}>+</Typography>
              </IconButton>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm container justifyContent="flex-end" spacing={2}>
          <Grid item xs={12} sm="auto">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#ffff',
                borderRadius: '8px',
                padding: '4px 16px',
                border: '1px solid #dedede',
              }}
            >
              <SearchOutlinedIcon sx={{ color: '#757575', marginRight: 1 }} />
              <InputBase
                placeholder="Search..."
                sx={{ flex: 1 }}
                onChange={handleSearchChange}
              />
            </Box>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleShareModalOpen}
              sx={{
                backgroundColor: '#000',
                color: '#fff',
                fontWeight: '500',
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#333',
                },
                display: 'flex',
                alignItems: 'center',
                '& .MuiButton-endIcon': {
                  transform: 'scaleX(-1)',
                },
              }}
              endIcon={<ReplyOutlinedIcon />}
            >
              Share Your Card
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <NewCardModalCustom
        isOpen={isNewCardModalOpen}
        onClose={handleNewCardModalClose}
        onCreate={handleCreateNewCard}
        anchorEl={plusButtonRef.current}
        horizontalOffset={20}
      />

      <CreateCardIndividualOrTeamMemberModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSelect={handleCardTypeSelect}
        anchorEl={plusButtonRef.current}
        offset={20}
        horizontalOffset={33}
      />

      <ShareYourCardModal
        open={isShareModalOpen}
        onClose={handleShareModalClose}
        profileUrl={profileUrl}
      />

      <UpgradeSnackBar
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        handleSubscribe={handleSubscribe}
      />
    </Box>
  );
};

const NewCardModalCustom = ({ isOpen, onClose, onCreate, anchorEl, horizontalOffset = 20 }) => {
  if (!isOpen) return null;

  const getModalPosition = () => {
    if (!anchorEl) return {};

    const anchorRect = anchorEl.getBoundingClientRect();
    const modalTop = anchorRect.top + (anchorRect.height / 2);
    const modalLeft = anchorRect.right + horizontalOffset;

    return {
      top: modalTop,
      left: modalLeft,
    };
  };

  const { top, left } = getModalPosition();

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
      }}
      onClick={handleOverlayClick}
    >
      <Box
        sx={{
          position: 'absolute',
          top: `${top}px`,
          left: `${left}px`,
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '1rem',
          width: '90%',
          maxWidth: '20rem',
          textAlign: 'center',
          boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-50%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Button
            variant="contained"
            onClick={onCreate}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              fontWeight: '500',
              borderRadius: '8px',
              textTransform: 'none',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
            startIcon={<Typography variant="h6" sx={{ fontSize: '22px' }}>+</Typography>}
          >
            Create New Card
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MyCardsNavbar;